<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  selectedSchedule: { type: Object, default: null },
});
const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const DEFAULT_RANGE = { from: '08:00', to: '18:00' };

const isEditing = computed(() => Boolean(props.selectedSchedule?.id));
const uiFlags = computed(() => getters['workSchedules/getUIFlags'].value);
const isSaving = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);

const name = ref(props.selectedSchedule?.name ?? '');

// One row per weekday, each holding the ranges of that day. A day with no range is a day off, and a day
// with two is a split shift — which is why the ranges are an array and not a single from/to pair: the
// backend already stores a row per range, and an editor that only knew one would quietly drop the second.
const days = ref(
  Array.from({ length: 7 }, (_, day) => ({
    day,
    ranges: (props.selectedSchedule?.shifts ?? [])
      .filter(shift => shift.day_of_week === day)
      .map(shift => ({ from: shift.from, to: shift.to })),
  }))
);

const dayLabels = computed(() =>
  Array.from({ length: 7 }, (_, day) => t(`WORK_SCHEDULES.DAYS.${day}`))
);

const toggleDay = dayRow => {
  dayRow.ranges = dayRow.ranges.length ? [] : [{ ...DEFAULT_RANGE }];
};

const addRange = dayRow => {
  dayRow.ranges.push({ ...DEFAULT_RANGE });
};

const removeRange = (dayRow, index) => {
  dayRow.ranges.splice(index, 1);
};

const shiftsPayload = computed(() =>
  days.value.flatMap(dayRow =>
    dayRow.ranges.map(range => ({
      day_of_week: dayRow.day,
      from: range.from,
      to: range.to,
    }))
  )
);

const isValid = computed(
  () =>
    name.value.trim() &&
    shiftsPayload.value.length &&
    shiftsPayload.value.every(shift => shift.from && shift.to)
);

const submit = async () => {
  if (!isValid.value) return;

  const payload = { name: name.value.trim(), shifts: shiftsPayload.value };
  try {
    if (isEditing.value) {
      await store.dispatch('workSchedules/update', {
        id: props.selectedSchedule.id,
        ...payload,
      });
      useAlert(t('WORK_SCHEDULES.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('workSchedules/create', payload);
      useAlert(t('WORK_SCHEDULES.ADD.API.SUCCESS_MESSAGE'));
    }
    emit('close');
  } catch (error) {
    // The backend refuses a duplicate name and a malformed time with a reason; showing it beats a generic
    // failure, which would leave the admin guessing which row is wrong.
    useAlert(error.message || t('WORK_SCHEDULES.ADD.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col gap-6 p-8 overflow-auto">
    <div class="flex flex-col gap-2">
      <h2 class="text-lg font-medium text-n-slate-12">
        {{
          isEditing
            ? t('WORK_SCHEDULES.EDIT.TITLE')
            : t('WORK_SCHEDULES.ADD.TITLE')
        }}
      </h2>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('WORK_SCHEDULES.FORM.DESCRIPTION') }}
      </p>
    </div>

    <Input
      v-model="name"
      :label="t('WORK_SCHEDULES.FORM.NAME.LABEL')"
      :placeholder="t('WORK_SCHEDULES.FORM.NAME.PLACEHOLDER')"
    />

    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('WORK_SCHEDULES.FORM.GRID.LABEL') }}
      </span>
      <div
        v-for="dayRow in days"
        :key="dayRow.day"
        class="flex flex-col gap-2 py-2 border-b border-n-weak last:border-b-0"
      >
        <div class="flex items-center gap-3">
          <Switch
            :model-value="dayRow.ranges.length > 0"
            @update:model-value="toggleDay(dayRow)"
          />
          <span class="w-28 text-sm text-n-slate-12">
            {{ dayLabels[dayRow.day] }}
          </span>
          <span
            v-if="!dayRow.ranges.length"
            class="text-sm text-n-slate-11 italic"
          >
            {{ t('WORK_SCHEDULES.FORM.GRID.CLOSED') }}
          </span>
        </div>

        <div
          v-for="(range, index) in dayRow.ranges"
          :key="index"
          class="flex items-center gap-2 ltr:ml-14 rtl:mr-14"
        >
          <input
            v-model="range.from"
            type="time"
            class="!mb-0 h-9 px-2 text-sm rounded-lg bg-n-alpha-black2 border-none outline outline-1 outline-offset-[-1px] outline-n-weak text-n-slate-12"
          />
          <span class="text-sm text-n-slate-11">
            {{ t('WORK_SCHEDULES.FORM.GRID.TO') }}
          </span>
          <input
            v-model="range.to"
            type="time"
            class="!mb-0 h-9 px-2 text-sm rounded-lg bg-n-alpha-black2 border-none outline outline-1 outline-offset-[-1px] outline-n-weak text-n-slate-12"
          />
          <Button
            v-tooltip.top="t('WORK_SCHEDULES.FORM.GRID.REMOVE_RANGE')"
            icon="i-lucide-x"
            ghost
            slate
            sm
            @click="removeRange(dayRow, index)"
          />
          <Button
            v-if="index === dayRow.ranges.length - 1"
            v-tooltip.top="t('WORK_SCHEDULES.FORM.GRID.ADD_RANGE')"
            icon="i-lucide-plus"
            ghost
            slate
            sm
            @click="addRange(dayRow)"
          />
        </div>
      </div>
    </div>

    <div class="flex justify-end gap-2">
      <Button faded slate :label="t('WORK_SCHEDULES.FORM.CANCEL')" @click="emit('close')" />
      <Button
        :label="t('WORK_SCHEDULES.FORM.SUBMIT')"
        :disabled="!isValid || isSaving"
        :is-loading="isSaving"
        @click="submit"
      />
    </div>
  </div>
</template>
